import {
  Component,
  ViewEncapsulation,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseListCtl, ServiceLocatorService } from '@ncs/ncs-lib';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatSnackBar } from '@angular/material/snack-bar';

export interface AppItem {
  id: number;
  name: string;
  appUrl: string;
  userName: string;
  orgName: string;
  bought: boolean | string;
  appStatus?: string;
}

interface TableColumn {
  key: string;
  label: string;
  sortable: boolean;
}

@Component({
  selector: 'app-app-store',
  templateUrl: './app-store.component.html',
  styleUrls: ['./app-store.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class AppStoreComponent
  extends BaseListCtl
  implements OnDestroy, AfterViewInit
{
  allColumns: TableColumn[] = [
    { key: 'sno', label: 'APP_STORE.TABLE.SNO', sortable: false },
    { key: 'name', label: 'APP_STORE.TABLE.NAME', sortable: true },
    { key: 'appUrl', label: 'APP_STORE.TABLE.URL', sortable: true },
    { key: 'userName', label: 'APP_STORE.TABLE.USER', sortable: true },
    { key: 'orgName', label: 'APP_STORE.TABLE.ORGANIZATION', sortable: true },
    { key: 'action', label: 'APP_STORE.TABLE.ACTION', sortable: false },
  ];

  defaultColumnOrder: string[] = this.allColumns.map((col) => col.key);
  displayedColumns: string[] = [...this.defaultColumnOrder];

  @ViewChild('input') input!: ElementRef;

  // Keep original data separate from displayed data
  private originalData: AppItem[] = [];
  isFiltered = false;

  constructor(
    public override locator: ServiceLocatorService,
    public override route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
    private snackBar: MatSnackBar,
  ) {
    super('', locator, route);

    let _self = this;
    locator.getPathVariable(route, function (params: any) {});

    _self.locator.config
      .getProperty('endpoint.application')
      .then((property: string) => {
        _self.initApiEndpoint(property);
      });
  }

  override ngOnInit() {
    this.restoreColumnOrderFromStorage();

    this.locator.config
      .getProperty('endpoint.application')
      .then((property: string) => {
        this.preload();
        this.search();
        this.display();
      });
  }

  ngAfterViewInit() {}

  getCurrentColumns(): TableColumn[] {
    return this.displayedColumns
      .map((key) => this.allColumns.find((col) => col.key === key))
      .filter((col) => col) as TableColumn[];
  }

  private restoreColumnOrderFromStorage() {
    const saved = localStorage.getItem('appStoreColumnOrder');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const requiredColumns = this.allColumns.map((col) => col.key);
        const isValid =
          requiredColumns.every((col) => parsed.includes(col)) &&
          parsed.length === requiredColumns.length;

        if (isValid) {
          this.displayedColumns = parsed;
        }
      } catch (e) {
        this.resetToDefaultOrder();
      }
    }
  }

  private resetToDefaultOrder() {
    this.displayedColumns = this.allColumns.map((col) => col.key);
    localStorage.removeItem('appStoreColumnOrder');
    this.cdRef.detectChanges();
  }

  drop(event: CdkDragDrop<string[]>) {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(
      this.displayedColumns,
      event.previousIndex,
      event.currentIndex,
    );

    localStorage.setItem(
      'appStoreColumnOrder',
      JSON.stringify(this.displayedColumns),
    );

    this.cdRef.detectChanges();
  }

  isColumnOrderChanged(): boolean {
    return (
      JSON.stringify(this.displayedColumns) !==
      JSON.stringify(this.defaultColumnOrder)
    );
  }

  restoreColumnOrder() {
    if (
      confirm(
        'Are you sure you want to reset all columns to their default positions?',
      )
    ) {
      this.resetToDefaultOrder();
      this.showSnackbar('Column order restored successfully!', 'success');
    }
  }

  override search() {
    this.display();
  }

  override preload() {}

  ngOnDestroy() {}

  getSerialNumber(index: number): number {
    return index + 1;
  }

  getAppStatus(element: any): string {
    if (element.bought === true || element.bought === 'true')
      return 'Purchased';
    if (element.bought === 'pending') return 'Pending';
    if (element.appStatus) return element.appStatus;
    return 'Purchased';
  }

  // Snackbar display method
  private showSnackbar(
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
  ) {
    const panelClass =
      type === 'success'
        ? 'success-snackbar'
        : type === 'error'
          ? 'error-snackbar'
          : 'info-snackbar';

    this.snackBar.open(message, 'Close', {
      duration: 4000,
      verticalPosition: 'top',
      // horizontalPosition: 'right',
      panelClass: [panelClass],
    });
  }

  display(callback?: any) {
    let _self = this;
    let url =
      this.locator.config.get('endpoint.application') +
      '/appstore/' +
      _self.form.pageNo;

    _self.form.searchParams['pageSize'] = _self.form.pageSize;

    console.log(_self.form.searchParams);

    this.locator.http.post(
      url,
      _self.form.searchParams,
      function (res: any, error: boolean) {
        if (error) {
          // Show error in snackbar
          _self.showSnackbar('Server Error: ' + error, 'error');
          // Also update form.message for alert box (original functionality)
          _self.form.message = 'Server Error:' + error;
          _self.form.error = true;
          return;
        }

        // Store original data
        _self.originalData = res.result.data || [];
        _self.form.list = [..._self.originalData]; // Copy for display
        _self.isFiltered = false;

        _self.form.pageCount = res.result.pageCount;
        _self.form.pageSize = res.result.pageSize;
        _self.form.pageNoList = [];

        // Build page number list (original functionality)
        for (let i = 0; i < _self.form.pageCount; i++) {
          _self.form.pageNoList.push(i);
        }

        // Check empty list (original functionality)
        if (_self.form.list.length == 0) {
          _self.form.message = 'No record found';
          _self.form.error = true;
        } else {
          _self.form.message = '';
          _self.form.error = false;
        }

        if (callback) {
          callback();
        }

        // Update form status (original functionality)
        _self.form.error = !res.success;
        _self.form.message = res.message || _self.form.message;

        _self.cdRef.detectChanges();
      },
    );
  }

  public buy(ele: any) {
    let id = ele.id;
    let _self = this;
    let url = this.locator.config.get('endpoint.userapps') + '/buy/' + id;

    this.locator.http.get(url, function (res: any, error: boolean) {
      if (error) {
        // Show error in snackbar
        _self.showSnackbar('Server Error: ' + error, 'error');
        // Also update form.message for alert box (original functionality)
        _self.form.message = 'Server Error:' + error;
        _self.form.error = true;
        return;
      }

      if (res.success) {
        ele['bought'] = 'pending';
        // Use message from server response
        _self.showSnackbar(res.message || 'Purchase request sent', 'success');
      } else {
        // Show error message from server
        _self.showSnackbar(res.message || 'Failed to purchase app', 'error');
      }

      // Update form status (original functionality)
      _self.form.error = !res.success;
      _self.form.message = res.message;

      _self.cdRef.detectChanges();
    });
  }

  isExpanded = false;

  toggleSearch(): void {
    this.isExpanded = !this.isExpanded;
  }

  sortedField: string | null = null;
  sortOrder: 'ASC' | 'DESC' | null = null;
  sortTable(field: string): void {
    let _self = this;
    if (this.sortedField === field) {
      this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.sortedField = field;
      this.sortOrder = 'ASC';
    }
    _self.form.searchParams['orderBy'] = this.sortedField;
    _self.form.searchParams['asc'] = this.sortOrder;
    _self.display();
  }

  //--------------search on App store----------------------

  public AppList: any[] = [];
  applicationName: string = '';
  appUrl: string = '';
  userName: string = '';
  orgName: string = '';

  searchApp() {
    console.log('Hello world : ');

    const url = this.locator.getEndpoint('endpoint.application') + '/appSearch';

    if (this.applicationName?.trim()) {
      this.form.searchParams['name'] = this.applicationName.trim();
    }

    if (this.appUrl?.trim()) {
      this.form.searchParams['appUrl'] = this.appUrl.trim();
    }

    if (this.userName?.toString().trim()) {
      this.form.searchParams['userName'] = this.userName.toString().trim();
    }

    if (this.orgName?.toString().trim()) {
      this.form.searchParams['orgName'] = this.orgName.toString().trim();
    }

    console.log('Search Payload : ', this.form.searchParams);

    this.locator.http.post(
      url,
      this.form.searchParams,
      (res: any, error: boolean) => {
        if (error) {
          alert('Server Error: ' + error);
          this.AppList = [];
          return;
        }

        this.AppList = res.result?.data || [];
        this.form.list = this.AppList;
        console.log('Filtered User List:', this.AppList);
      },
    );
  }

  resetFilters() {
    this.applicationName = '';
    this.appUrl = '';
    this.userName = '';
    this.orgName = '';

    // 🔹 Clear backend search params
    this.form.searchParams = {};
    this.form.pageNo = 0;

    this.display();
  }

  // Search Field New dynamic Implementation

  searchData: any = {};
  searchList: any[] = [];
  searchViewList: any = [];
  searchView: any = {};

  fields = [
    { label: 'Application Name', value: 'name' },
    { label: 'App Url', value: 'appUrl' },
    { label: 'User Name', value: 'userName' },
    { label: 'Organization Name', value: 'orgName' },
  ];

  operators = [
    { label: '=', value: 'EQ' },
    { label: '!=', value: 'NEQ' },
    { label: '>', value: 'GT' },
    { label: '<', value: 'LT' },
    { label: '>=', value: 'GTE' },
    { label: '<=', value: 'LTE' },
    { label: 'like', value: 'LIKE' },
    { label: 'between', value: 'BETWEEN' },
    { label: 'Not Null', value: 'NOT NULL' },
    { label: 'Null', value: 'NULL' },
    { label: 'Empty', value: 'EMPTY' },
    { label: 'Day', value: 'DAY' },
    { label: 'Month', value: 'MONTH' },
    { label: 'Day & Month', value: 'DAY_AND_MONTH' },
  ];

  // Component mein
  selectedDay: string = 'Today';
  selectedMonth: string = 'CURRENT';

  onConditionChange() {
    // Reset selections when condition changes
    this.selectedDay = '';
    this.selectedMonth = '';
  }

  addSearchData() {
    if (!this.searchData.field || !this.searchData.condition) {
      return;
    }

    const payload: any = {
      field: this.searchData.field,
      condition: this.searchData.condition,
    };

    // Handle BETWEEN
    if (this.searchData.condition === 'BETWEEN') {
      payload.rangeFrom = this.searchData.rangeFrom;
      payload.rangeTo = this.searchData.rangeTo;
    }
    // Handle NULL / EMPTY
    else if (
      ['NULL', 'NOT NULL', 'EMPTY'].includes(this.searchData.condition)
    ) {
      payload.value = null;
    }
    // Normal case
    else {
      payload.value = this.searchData.value;
    }

    this.operators.forEach(async (e: any) => {
      if (this.searchData['condition'] === e.value) {
        let searchViewData = {
          field: this.searchData['field'],
          condition: e.label,
          value: this.searchData.value,
          rangeFrom: this.searchData['rangeFrom'],
          rangeTo: this.searchData['rangeTo'],
        };
        this.searchViewList.push(searchViewData);
      }
    });
    console.log('search values : ', this.searchViewList);
    this.searchList.push(payload);

    console.log('data on it : ', this.searchList);

    // Reset current input
    this.searchData = {};
  }

  removeSearchData(index: any) {
    if (index > -1) this.searchList.splice(index, 1);
    if (index > -1) this.searchViewList.splice(index, 1);
  }

  resetSearchCriteria() {
    this.searchList = [];
    this.searchData = {};
    this.searchView = {};
    this.searchViewList = [];
    this.form.searchParams = {};
  }

  advanceSearch() {
    const url =
      this.locator.getEndpoint('endpoint.application') +
      '/advanceSearch/' +
      this.form.pageNo;

    this.form.searchParams = {
      filters: this.searchList,
    };

    this.form.searchParams['pageSize'] = this.form.pageSize;

    console.log('Advance Search Payload:', this.form.searchParams);

    // 2. Execute the HTTP POST request using your locator service
    this.locator.http.post(
      url,
      this.form.searchParams,
      (res: any, error: boolean) => {
        if (error) {
          alert('Server Error: ' + error);
          this.AppList = [];
          return;
        }

        // 3. Update the list with filtered results
        this.AppList = res.result?.data || [];
        this.form.list = this.AppList;

        console.log('Filtered Results:', this.AppList);
      },
    );
  }
}
